import { NgClass } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { ContentCardComponent } from '@shared/components/content-card/content-card.component';
import { TipoEntidadeFormulaEnum } from '@shared/enums/formula/tipo-entidade-formula.enum';
import { FormulaCicloEntidadeStruct } from '@shared/services/structs/formula/formula-ciclo-entidade.struct';
import { FormulaCicloMembroStruct } from '@shared/services/structs/formula/formula-ciclo-membro.struct';
import { FormulaOrigemStruct } from '@shared/services/structs/formula/formula-origem.struct';
import { FormulaCicloDialogData } from './formula-ciclo-dialog-data';
import { FormulaCicloDialogResult } from './formula-ciclo-dialog-result';
import { FormulaCicloMembroResult } from './formula-ciclo-membro-result';

export { FormulaCicloDialogData } from './formula-ciclo-dialog-data';
export { FormulaCicloDialogResult } from './formula-ciclo-dialog-result';
export { FormulaCicloMembroResult } from './formula-ciclo-membro-result';

/**
 * Configura o rascunho de ciclo da entidade em edição a partir do ciclo detectado pela API. Só as fórmulas que
 * cruzam tributos são opções de início. Modo simples: o usuário escolhe uma (arrastando para o topo ou clicando)
 * e se ela é recalculada no final. Modo vários inícios (nenhuma sozinha resolve): escolhe quantas forem
 * necessárias, na ordem em que são calculadas, cada uma com o seu "Recalcular no final".
 */
@Component({
    selector: 'app-formula-ciclo-dialog',
    standalone: true,
    imports: [NgClass, FormsModule, DragDropModule, MatDialogModule, MatButtonModule, MatIconModule,
        MatExpansionModule, MatSlideToggleModule, MatTooltipModule, AlertCardComponent, ContentCardComponent],
    templateUrl: './formula-ciclo-dialog.component.html',
    styleUrl: './formula-ciclo-dialog.component.css',
})
export class FormulaCicloDialogComponent {
    /** Fórmulas escolhidas para começar o cálculo, na ordem em que são calculadas (uma só no modo simples). */
    public inicios: FormulaCicloMembroStruct[] = [];
    /** Opções de início ainda não escolhidas, na ordem do ciclo. */
    public restantes: FormulaCicloMembroStruct[] = [];
    /** Todos os membros: os inícios na ordem e depois os demais em ordem de dependência. Só leitura na tela. */
    public envolvidas: FormulaCicloMembroStruct[] = [];
    /** Modo vários inícios: sem os inícios escolhidos ainda sobra ciclo entre os membros. */
    public cicloPendente = false;
    /** "Recalcular no final" de cada início, pela chave da variável; padrão ligado. */
    public recalcular: Record<string, boolean> = {};

    /** Modo simples: só o topo aceita soltar. Vários inícios: qualquer posição da área dos inícios. */
    public readonly aceitarPosicaoInicio = (indice: number): boolean => this.isVariosInicios || indice === 0;
    /** Só o modo vários inícios aceita devolver uma opção arrastando-a para fora da área dos inícios. */
    public readonly aceitarEmOpcoes = (): boolean => this.isVariosInicios;

    /** Fórmulas oferecidas como início: as que cruzam tributos; sem a informação da API, as que resolvem; senão todas. */
    private readonly opcoes: FormulaCicloMembroStruct[];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: FormulaCicloDialogData,
        private dialogRef: MatDialogRef<FormulaCicloDialogComponent, FormulaCicloDialogResult>,
    ) {
        const membros = data.ciclo.membros;
        const comOpcao = membros.filter(membro => membro.isOpcaoInicio === true);
        const comInicioPossivel = membros.filter(membro => membro.isInicioPossivel === true);
        this.opcoes = comOpcao.length > 0 ? comOpcao : comInicioPossivel.length > 0 ? comInicioPossivel : membros;

        const escolhidos = this.escolherIniciosIniciais();
        for (const ponto of data.ciclo.configuracaoAtual?.pontos ?? []) {
            this.recalcular[ponto.chaveVariavel] = ponto.isRecalcularAposFechamento;
        }
        this.reorganizar(escolhidos);
    }

    /** O ciclo exige mais de um início; caso contrário uma só fórmula resolve. */
    public get isVariosInicios(): boolean {
        return this.data.ciclo.isVariosInicios === true;
    }

    /** Aviso de confirmação: o ciclo já configurado mudou de forma. */
    public get isCicloAlterado(): boolean {
        return !!this.data.ciclo.configuracaoAtual;
    }

    public get podeSalvar(): boolean {
        return this.inicios.length > 0 && !this.cicloPendente;
    }

    /** Entidades donas das fórmulas do ciclo, na ordem recebida; vazio quando o ciclo só tem fórmulas de modelo. */
    public get entidadesDonas(): FormulaCicloEntidadeStruct[] {
        return this.data.ciclo.entidades ?? [];
    }

    /** Badge de cada dona do ciclo; a dona em edição (sem código) é chamada de "Esta natureza"/"Esta regra"/"Este DIFAL". */
    public rotuloDona(dona: FormulaCicloEntidadeStruct): string {
        const edicao = this.isDonaEdicao(dona);
        const comNome = (prefixo: string): string => [prefixo, dona.nomeEntidade].filter(Boolean).join(' - ');
        switch (dona.tipoEntidade) {
            case TipoEntidadeFormulaEnum.NaturezaOperacao:
            case TipoEntidadeFormulaEnum.ExcecaoNaturezaOperacao:
                return edicao ? 'Esta natureza' : comNome('Natureza');
            case TipoEntidadeFormulaEnum.RegraTributaria:
                return edicao ? 'Esta regra' : comNome('Regra tributária');
            case TipoEntidadeFormulaEnum.Difal:
                return edicao ? 'Este DIFAL' : comNome('DIFAL');
            default:
                return dona.nomeEntidade ?? '';
        }
    }

    public classeDona(dona: FormulaCicloEntidadeStruct): string {
        return this.isDonaEdicao(dona) ? 'badge-azul' : 'badge-cinza';
    }

    public descricaoFormula(membro: FormulaCicloMembroStruct): string {
        return membro.origem?.descricaoFormula || membro.chaveVariavel;
    }

    /** Badge da entidade dona da fórmula; nulo quando a variável não tem fórmula no conjunto analisado. */
    public rotuloEntidade(membro: FormulaCicloMembroStruct): string | null {
        const origem = membro.origem;
        if (!origem) return null;
        const comNome = (prefixo: string): string => [prefixo, origem.nomeEntidade].filter(Boolean).join(' - ');
        switch (origem.tipoEntidade) {
            case TipoEntidadeFormulaEnum.PadraoSistema:
                return 'Modelo padrão do sistema';
            case TipoEntidadeFormulaEnum.ModeloEmpresa:
                return 'Modelo da empresa';
            case TipoEntidadeFormulaEnum.NaturezaOperacao:
                return this.isEntidadeEdicao(origem) ? 'Esta natureza' : comNome('Natureza');
            case TipoEntidadeFormulaEnum.ExcecaoNaturezaOperacao:
                return this.isEntidadeEdicao(origem) ? 'Exceção desta natureza' : comNome('Exceção da natureza');
            case TipoEntidadeFormulaEnum.RegraTributaria:
                return this.isEntidadeEdicao(origem) ? 'Esta regra' : comNome('Regra tributária');
            case TipoEntidadeFormulaEnum.Difal:
                return this.isEntidadeEdicao(origem) ? 'Este DIFAL' : comNome('DIFAL');
            case TipoEntidadeFormulaEnum.ConfiguracaoCustoVenda:
                return 'Custo e venda';
            default:
                return origem.nomeEntidade;
        }
    }

    public classeEntidade(membro: FormulaCicloMembroStruct): string {
        return membro.origem && this.isEntidadeEdicao(membro.origem) ? 'badge-azul' : 'badge-cinza';
    }

    /**
     * Modo simples: soltar uma opção no topo troca o início. Vários inícios: soltar na área dos inícios adiciona
     * (ou reordena) e soltar fora dela remove. Qualquer outro movimento é ignorado.
     */
    public soltar(evento: CdkDragDrop<FormulaCicloMembroStruct[]>): void {
        const origem = evento.previousContainer.data;
        const membro = origem[evento.previousIndex];
        if (!membro) return;
        const origemInicios = origem === this.inicios;
        const destinoInicios = evento.container.data === this.inicios;
        if (origemInicios && destinoInicios) {
            if (!this.isVariosInicios || evento.currentIndex === evento.previousIndex) return;
            const reordenados = [...this.inicios];
            moveItemInArray(reordenados, evento.previousIndex, evento.currentIndex);
            this.reorganizar(reordenados);
        } else if (destinoInicios) {
            this.adicionarInicio(membro, evento.currentIndex);
        } else if (origemInicios) {
            this.removerInicio(membro);
        }
    }

    /** Modo simples: a opção vira o único início. Vários inícios: entra na posição indicada (padrão: no fim). */
    public adicionarInicio(membro: FormulaCicloMembroStruct, indice: number = this.inicios.length): void {
        if (this.inicios.includes(membro)) return;
        if (!this.isVariosInicios) {
            this.reorganizar([membro]);
            return;
        }
        const novos = [...this.inicios];
        novos.splice(indice, 0, membro);
        this.reorganizar(novos);
    }

    /** Só no modo vários inícios: devolve o início à lista de opções. */
    public removerInicio(membro: FormulaCicloMembroStruct): void {
        if (!this.isVariosInicios) return;
        this.reorganizar(this.inicios.filter(item => item !== membro));
    }

    public confirmar(): void {
        if (!this.podeSalvar) return;
        const membros: FormulaCicloMembroResult[] = this.envolvidas.map(membro => {
            const indice = this.inicios.indexOf(membro);
            return {
                chaveVariavel: membro.chaveVariavel,
                isPontoDecisao: indice >= 0,
                ordemCalculo: indice >= 0 ? indice + 1 : null,
                isRecalcularAposFechamento: indice >= 0 ? this.recalcular[membro.chaveVariavel] ?? true : null,
            };
        });
        this.dialogRef.close({
            codigo: this.data.ciclo.configuracaoAtual?.codigo ?? null,
            nome: null,
            descricao: null,
            dataVigenciaInicio: this.data.ciclo.dataVigenciaInicio ?? null,
            membros,
            entidades: this.data.ciclo.entidades ?? [],
        });
    }

    /**
     * Inícios de partida, nesta prioridade: os da configuração atual (ciclo alterado), os sugeridos pela API (vários
     * inícios) e a primeira opção do caminho (simples). No modo simples só o primeiro vale. A configuração atual só é
     * usada quando todos os inícios dela continuam entre as opções; senão vale a sugestão.
     */
    private escolherIniciosIniciais(): FormulaCicloMembroStruct[] {
        const ciclo = this.data.ciclo;
        const achar = (chave: string): FormulaCicloMembroStruct | undefined =>
            this.opcoes.find(membro => membro.chaveVariavel === chave);
        const existe = (membro: FormulaCicloMembroStruct | undefined): membro is FormulaCicloMembroStruct => !!membro;

        const pontosAtuais = [...(ciclo.configuracaoAtual?.pontos ?? [])].sort((a, b) => a.ordemCalculo - b.ordemCalculo);
        const atuaisNasOpcoes = pontosAtuais.map(ponto => achar(ponto.chaveVariavel)).filter(existe);
        const atuais = atuaisNasOpcoes.length === pontosAtuais.length ? atuaisNasOpcoes : [];
        const sugeridos = this.isVariosInicios ? (ciclo.iniciosSugeridos ?? []).map(achar).filter(existe) : [];
        const escolhidos = atuais.length > 0 ? atuais : sugeridos;
        if (this.isVariosInicios) return escolhidos;
        if (escolhidos.length > 0) return [escolhidos[0]];

        const primeiroDoCaminho = ciclo.caminho
            .map(chave => this.opcoes.find(membro => membro.chaveVariavel === chave))
            .find(existe);
        const inicio = primeiroDoCaminho ?? this.opcoes[0];
        return inicio ? [inicio] : [];
    }

    private reorganizar(inicios: FormulaCicloMembroStruct[]): void {
        const escolhidas = new Set(inicios.map(membro => membro.chaveVariavel));
        for (const chave of escolhidas) this.recalcular[chave] ??= true;
        this.inicios = inicios;
        this.restantes = this.opcoes.filter(membro => !escolhidas.has(membro.chaveVariavel));
        this.cicloPendente = this.isVariosInicios && this.sobraCiclo(escolhidas);
        this.envolvidas = this.ordenarEnvolvidas();
    }

    /** Sem os inícios escolhidos, ainda há ciclo entre os membros (busca em profundidade pelas dependências). */
    private sobraCiclo(escolhidas: Set<string>): boolean {
        const ativos = new Map(this.data.ciclo.membros
            .filter(membro => !escolhidas.has(membro.chaveVariavel))
            .map(membro => [membro.chaveVariavel, membro] as const));
        const estado = new Map<string, 'visitando' | 'visitado'>();
        const acharCiclo = (chave: string): boolean => {
            const atual = estado.get(chave);
            if (atual === 'visitando') return true;
            if (atual === 'visitado') return false;
            estado.set(chave, 'visitando');
            for (const dependencia of ativos.get(chave)?.dependencias ?? []) {
                if (ativos.has(dependencia) && acharCiclo(dependencia)) return true;
            }
            estado.set(chave, 'visitado');
            return false;
        };
        return [...ativos.keys()].some(chave => acharCiclo(chave));
    }

    /**
     * Inícios na ordem escolhida e depois os demais. Modo simples: na sequência de cálculo da API (ou no caminho a
     * partir do início, sem ela). Vários inícios: em ordem de dependência (o que a fórmula lê vem antes dela).
     */
    private ordenarEnvolvidas(): FormulaCicloMembroStruct[] {
        const ciclo = this.data.ciclo;
        const ordenados = [...this.inicios];
        const incluir = (membro: FormulaCicloMembroStruct | undefined): void => {
            if (membro && !ordenados.includes(membro)) ordenados.push(membro);
        };

        if (!this.isVariosInicios) {
            const inicio = this.inicios[0];
            let chaves = inicio?.sequenciaCalculo ?? [];
            if (inicio && chaves.length === 0) {
                const posicao = ciclo.caminho.indexOf(inicio.chaveVariavel);
                chaves = posicao < 0 ? ciclo.caminho : [...ciclo.caminho.slice(posicao), ...ciclo.caminho.slice(0, posicao)];
            }
            chaves.forEach(chave => incluir(ciclo.membros.find(membro => membro.chaveVariavel === chave)));
            ciclo.membros.forEach(incluir);
            return ordenados;
        }

        const visitados = new Set(ordenados);
        const visitar = (membro: FormulaCicloMembroStruct): void => {
            if (visitados.has(membro)) return;
            visitados.add(membro);
            for (const dependencia of membro.dependencias ?? []) {
                const alvo = ciclo.membros.find(item => item.chaveVariavel === dependencia);
                if (alvo) visitar(alvo);
            }
            ordenados.push(membro);
        };
        ciclo.caminho.forEach(chave => {
            const membro = ciclo.membros.find(item => item.chaveVariavel === chave);
            if (membro) visitar(membro);
        });
        ciclo.membros.forEach(visitar);
        return ordenados;
    }

    /** Dona em edição: sem código e do mesmo tipo da entidade que a tela edita (a exceção conta como natureza). */
    private isDonaEdicao(dona: FormulaCicloEntidadeStruct): boolean {
        if (dona.codigoEntidade != null) return false;
        const tipo = dona.tipoEntidade === TipoEntidadeFormulaEnum.ExcecaoNaturezaOperacao
            ? TipoEntidadeFormulaEnum.NaturezaOperacao
            : dona.tipoEntidade;
        return this.data.tipoEntidadeEdicao == null || tipo === this.data.tipoEntidadeEdicao;
    }

    /**
     * Entidade em edição: id nulo (cadastro ainda sem id) ou igual ao informado pela tela. A exceção pertence à
     * natureza, por isso é comparada como natureza.
     */
    private isEntidadeEdicao(origem: FormulaOrigemStruct): boolean {
        if (origem.idEntidade == null) return true;
        if (this.data.idEntidadeEdicao == null) return false;
        const tipo = origem.tipoEntidade === TipoEntidadeFormulaEnum.ExcecaoNaturezaOperacao
            ? TipoEntidadeFormulaEnum.NaturezaOperacao
            : origem.tipoEntidade;
        return origem.idEntidade === this.data.idEntidadeEdicao
            && (this.data.tipoEntidadeEdicao == null || tipo === this.data.tipoEntidadeEdicao);
    }
}
